import React, { useState, useEffect } from 'react';
import { Card, Button, Input } from '@/components';
import { getToken } from '@/utils';
import { buildApiUrl } from '@/utils/api';

interface ShopMemberProfile {
  id: string;
  user_id: string;
  email: string;
  role: 'owner' | 'staff';
  is_active: boolean;
  created_at: string;
}

interface ShopSettingsTabProps {
  shopId: string;
  isOwner: boolean;
}

export const ShopSettingsTab: React.FC<ShopSettingsTabProps> = ({ shopId, isOwner }) => {
  const [members, setMembers] = useState<ShopMemberProfile[]>([]);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tempCredentials, setTempCredentials] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (shopId) {
      fetchMembers();
    }
  }, [shopId]);

  const fetchMembers = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(buildApiUrl(`shops/${shopId}/members`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        console.error('Failed to fetch members:', response.status);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setError(null);
    setSuccess(null);
    setTempCredentials(null);
    setIsSubmitting(true);

    try {
      const token = getToken();
      const response = await fetch(buildApiUrl(`shops/${shopId}/members`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: email.trim(),
          role: 'staff',
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Staff member added successfully!');
        setTempCredentials(data.credentials);
        setEmail('');
        fetchMembers();
      } else {
        setError(data.error || 'Failed to add staff member.');
      }
    } catch (err) {
      setError('An error occurred while adding the staff member.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveStaff = async (memberId: string, memberEmail: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from staff?`)) return;

    setError(null);
    setSuccess(null);

    try {
      const token = getToken();
      const response = await fetch(buildApiUrl(`shops/${shopId}/members/${memberId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess('Staff member removed successfully.');
        fetchMembers();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to remove staff member.');
      }
    } catch (err) {
      setError('An error occurred while removing the staff member.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Staff Section (Owner Only) */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-slate-950 mb-2">Add New Staff Member</h3>
        <p className="text-slate-600 text-sm mb-4">
          Register a new staff member to access this shop's dashboard and orders.
        </p>

        {isOwner ? (
          <>
            {error && (
              <div className="p-3 mb-4 text-sm text-red-800 bg-red-50 rounded border border-red-200" role="alert">
                ✕ {error}
              </div>
            )}
            {success && (
              <div className="p-3 mb-4 text-sm text-green-800 bg-green-50 rounded border border-green-200" role="alert">
                ✓ {success}
              </div>
            )}
            {tempCredentials && (
              <div className="p-4 mb-4 text-sm text-blue-900 bg-blue-50 rounded border border-blue-200 space-y-2">
                <p className="font-semibold text-blue-950">⚠️ Temporary Login Credentials Created:</p>
                <div className="rounded bg-white p-3 font-mono border border-blue-100 space-y-1">
                  <p>Email: <span className="font-semibold">{tempCredentials.email}</span></p>
                  <p>Password: <span className="font-semibold">{tempCredentials.password}</span></p>
                </div>
                <p className="text-xs text-blue-600 font-medium">
                  Please copy and share these credentials securely with your staff member. They will use these to sign in.
                </p>
              </div>
            )}

            <form onSubmit={handleAddStaff} className="flex gap-4 items-end max-w-lg">
              <div className="flex-1">
                <Input
                  id="staffEmail"
                  label="Email Address"
                  type="email"
                  name="staffEmail"
                  placeholder="staff@printnow.local"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  fullWidth
                />
              </div>
              <Button type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
                Add Staff
              </Button>
            </form>
          </>
        ) : (
          <div className="rounded-lg bg-slate-50 p-4 border border-slate-200">
            <p className="text-sm font-medium text-slate-500 flex items-center gap-1.5 justify-center py-2">
              🔒 Accessed by owner (Only the shop owner can add staff members)
            </p>
          </div>
        )}
      </Card>

      {/* Current Members List */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-slate-950 mb-2">Current Shop Members</h3>
        <p className="text-slate-600 text-sm mb-4">
          The following users have access to view and manage orders for this shop.
        </p>

        {isLoading ? (
          <div className="py-8 text-center text-slate-500 text-sm">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 mr-2"></div>
            Loading members...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Joined At</th>
                  {isOwner && <th className="px-4 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3.5 font-medium text-slate-950">{m.email}</td>
                    <td className="px-4 py-3.5 capitalize">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        m.role === 'owner' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {m.role}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full ${m.is_active ? 'bg-green-500' : 'bg-slate-400'}`}></span>
                        {m.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-slate-500">
                      {new Date(m.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    {isOwner && (
                      <td className="px-4 py-3.5 text-right">
                        {m.role !== 'owner' ? (
                          <button
                            onClick={() => handleRemoveStaff(m.id, m.email)}
                            className="text-red-600 hover:text-red-700 font-semibold text-xs transition-colors"
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="text-slate-400 text-xs font-medium">Owner</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ShopSettingsTab;
